import BackButton from 'components/Analytics/BackButton'
import { ButtonError, ButtonLight, ButtonOutlined, ButtonPrimary } from 'components/Button'
import Card from 'components/Card'
import Column from 'components/Column'
import RewardTokenSelector, { CustomInput } from 'components/KodiakFarms/RewardSelector'
import QuestionHelper from 'components/QuestionHelper'
import Row, { RowBetween } from 'components/Row'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import { secondsInMonth } from 'date-fns'
import { useAllTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useFarmDeployment } from 'lib/hooks/kodiakFarms/useFarmDeployment'
import { useRewardBalances } from 'lib/hooks/kodiakFarms/useRewardBalances'
import { useTokenInfo } from 'lib/hooks/kodiakFarms/useTokenInfo'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Minus, Plus } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import { Text } from 'rebass'
import { useToggleWalletModal } from 'state/application/hooks'
import styled from 'styled-components'
import { Separator, ThemedText } from 'theme'
import { formatUnits } from 'viem'

import { MAX_REWARD_TOKENS } from './constants'
import DeployFarmConfigStep from './DeployFarmConfigStep'
import { calculateRewardRatePerSecond } from './helper'

const Container = styled(Card)`
  width: 100%;
  max-width: 45rem;
  margin: 0 auto;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  background: ${({ theme }) => theme.cardPrimary};
  overflow-x: hidden;
`

const CardHeader = styled.div`
  padding: 0.5rem 1.5rem;
`

const CardTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`

const CardDescription = styled.p`
  color: ${(props) => props.theme.textSecondary};
  font-size: 0.875rem;
`

const CardContent = styled.div`
  width: 100%;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const CardFooter = styled.div`
  padding: 1.5rem;
  width: 100%;
`

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props) => props.theme.textSecondary};
`

const Alert = styled(Container)`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  color: ${(props) => props.theme.textSecondary};
  font-size: 0.875rem;
`

const DeployForm: React.FC = () => {
  const [stakingToken, setStakingToken] = useState('')
  const [showStartModal, setShowStartModal] = useState(false)
  const [deployingFarm, setDeployingFarm] = useState(false)
  const [deployHash, setDeployHash] = useState('')
  const navigate = useNavigate()

  const { account } = useConnection()
  const [rewardTokens, setRewardTokens] = useState([''])
  const [rewardManagers, setRewardManagers] = useState([account || ''])
  const [monthlyRewards, setMonthlyRewards] = useState([''])
  const allTokens = useAllTokens()
  const toggleWalletModal = useToggleWalletModal()
  const [rewardPeriods, setRewardPeriods] = useState<Array<number>>([secondsInMonth])

  const { deploy, isDeploying, error: deployError, farmAddress } = useFarmDeployment()
  const {
    tokenInfo: stakingTokenInfo,
    error: stakingTokenError,
    fetchTokenInfo: fetchStakingTokenInfo,
    clearTokenInfo,
  } = useTokenInfo()

  useEffect(() => {
    if (account) {
      setRewardManagers([account])
    }
  }, [account])

  const rewardData = useMemo(
    () =>
      rewardTokens.map((token, i) => ({
        token,
        amount: monthlyRewards[i],
        period: rewardPeriods[i],
        decimals: allTokens[token]?.decimals || 18,
      })),
    [rewardTokens, monthlyRewards, rewardPeriods, allTokens]
  )

  const { hasInsufficientBalance, insufficientTokens, isLoading, error } = useRewardBalances(rewardData)

  const addRewardToken = () => {
    if (rewardTokens.length < MAX_REWARD_TOKENS) {
      setRewardTokens([...rewardTokens, ''])
      setRewardManagers([...rewardManagers, account ?? ''])
      setRewardPeriods([...rewardPeriods, secondsInMonth])
      setMonthlyRewards([...monthlyRewards, ''])
    }
  }

  const removeRewardToken = (index: number) => {
    setRewardTokens(rewardTokens.filter((_, i) => i !== index))
    setRewardManagers(rewardManagers.filter((_, i) => i !== index))
    setRewardPeriods(rewardPeriods.filter((_, i) => i !== index))
    setMonthlyRewards(monthlyRewards.filter((_, i) => i !== index))
  }

  const resetForm = useCallback(() => {
    setStakingToken('')
    setRewardTokens([''])
    setRewardManagers([''])
    setMonthlyRewards([''])
    setRewardPeriods([secondsInMonth])
  }, [])

  const handleDeploy = useCallback(async () => {
    // Filter out any empty entries
    const filteredTokens = rewardTokens.filter((token) => token !== '')
    const filteredManagers = rewardManagers.filter((_, i) => rewardTokens[i] !== '')
    const filteredPeriods = rewardPeriods.filter((_, i) => rewardTokens[i] !== '')
    const filteredMonthly = monthlyRewards.filter((_, i) => rewardTokens[i] !== '')

    setDeployingFarm(true)

    // Calculate reward rates using proper decimals for each token
    const rewardRates = filteredMonthly.map((reward, i) =>
      calculateRewardRatePerSecond(reward, filteredPeriods[i], allTokens[filteredTokens[i]]?.decimals || 18)
    )

    try {
      const txHash = await deploy({
        stakingToken,
        rewardTokens: filteredTokens,
        rewardManagers: filteredManagers,
        rewardRates,
      })

      if (txHash) {
        setDeployHash(txHash)
      }
      // resetForm()
    } catch (err) {
      console.log(error)
      throw error
    } finally {
      setDeployingFarm(false)
    }
  }, [rewardTokens, rewardManagers, rewardPeriods, monthlyRewards, deploy, stakingToken, error])
  const deployConfigStep = useStepWithNext('deployFarmConfig', DeployFarmConfigStep, {
    farmAddress: farmAddress || '',
    onStart: handleDeploy,
    attemptingTxn: deployingFarm,
    hash: deployHash,
    autoConfirm: false,
    waitForConfirmation: true,
  })

  const steps = [deployConfigStep]

  const isDeployDisabled = useMemo(() => {
    return (
      isDeploying ||
      !stakingToken ||
      rewardTokens.every((token) => !token) ||
      rewardManagers.every((manager) => !manager) ||
      Boolean(stakingTokenError)
    )
  }, [isDeploying, stakingToken, rewardTokens, rewardManagers, stakingTokenError])

  const handleConfirm = () => {
    if (farmAddress) navigate(`/liquidity/farms/configure/${farmAddress}`)
    setShowStartModal(false)
  }

  return (
    <>
      <StepLineModal
        isOpen={showStartModal}
        onDismiss={handleConfirm}
        steps={steps}
        header={<ThemedText.DeprecatedMediumHeader>Deploy Farm</ThemedText.DeprecatedMediumHeader>}
        onFinish={handleConfirm}
      />
      <div style={{ width: '100%', maxWidth: '45rem', margin: '0 auto 0.5rem' }}>
        <BackButton fallbackURI="/liquidity/farms" title="Farms" />
      </div>
      <Container>
        <CardHeader>
          <CardTitle>Deploy Kodiak Farm</CardTitle>
          <CardDescription>Deploy a new farm contract to distribute rewards for staked tokens</CardDescription>
        </CardHeader>
        <CardContent>
          <FormSection>
            <FormGroup>
              <Label>Staking Token Address</Label>
              <CustomInput
                placeholder="Enter LP token address (0x...)"
                value={stakingToken}
                onChange={(e) => {
                  const address = e.target.value
                  setStakingToken(address)
                  fetchStakingTokenInfo(address)
                  clearTokenInfo()
                }}
              />
              {stakingTokenError ? (
                <p style={{ fontSize: '12px', color: 'red' }}>{stakingTokenError}</p>
              ) : (
                stakingTokenInfo && (
                  <Text fontSize={12} color="textSecondary">
                    {stakingTokenInfo.name} ({stakingTokenInfo.symbol})
                  </Text>
                )
              )}
            </FormGroup>
            <Separator />

            <FormSection>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text fontSize={18} fontWeight="600">
                  Reward Token
                </Text>
                {rewardTokens.length < MAX_REWARD_TOKENS && (
                  <ButtonOutlined onClick={addRewardToken} style={{ maxWidth: '160px', padding: '4px 8px' }}>
                    <RowBetween>
                      <Plus size={16} />
                      <span style={{ fontSize: '14px' }}>Add Reward Token</span>
                    </RowBetween>
                  </ButtonOutlined>
                )}
              </div>
              {rewardTokens.map((token, index) => (
                <Container key={index} style={{ padding: '24px', overflowX: 'unset' }}>
                  <Column style={{ gap: '24px' }}>
                    <FormGroup>
                      <Label>Reward Token Address</Label>
                      <RewardTokenSelector
                        value={token}
                        onChange={(value) => {
                          const newTokens = [...rewardTokens]
                          newTokens[index] = value
                          setRewardTokens(newTokens)
                        }}
                        placeholder="Select reward token"
                        customPlaceholder="Enter reward token address (0x...)"
                        allTokens={allTokens}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Reward Manager Address</Label>
                      <CustomInput
                        placeholder="0x..."
                        value={rewardManagers[index]}
                        onChange={(e) => {
                          const newManagers = [...rewardManagers]
                          newManagers[index] = e.target.value
                          setRewardManagers(newManagers)
                        }}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Row>
                        <Label>Monthly Reward Amount</Label>
                        <QuestionHelper text="Enter monthly token distribution amount. The amount will be evenly distributed per second (e.g. 10,000 tokens = ~0.00386 tokens/sec). Distribution auto-renews monthly if contract has sufficient balance." />
                      </Row>
                      <CustomInput
                        type="number"
                        min="0"
                        step="any"
                        placeholder="Amount to distribute per month"
                        value={monthlyRewards[index]}
                        onChange={(e) => {
                          const newMonthlyRewards = [...monthlyRewards]
                          newMonthlyRewards[index] = e.target.value
                          setMonthlyRewards(newMonthlyRewards)
                        }}
                      />
                      <RowBetween>
                        {token && monthlyRewards[index] && (
                          <Text fontSize={12} key={index} color="textTertiary">
                            {`(${Number(
                              formatUnits(
                                calculateRewardRatePerSecond(
                                  monthlyRewards[index],
                                  rewardPeriods[index],
                                  allTokens[token]?.decimals || 18
                                ),
                                allTokens[token]?.decimals || 18
                              )
                            ).toFixed(8)} ${allTokens[token]?.symbol || 'tokens'}/s)`}
                          </Text>
                        )}
                      </RowBetween>
                    </FormGroup>
                  </Column>
                  {index > 0 && (
                    <ButtonError
                      error
                      onClick={() => removeRewardToken(index)}
                      style={{ marginTop: '1rem', gap: '4px' }}
                    >
                      <Minus size={16} />
                      Remove Token
                    </ButtonError>
                  )}
                </Container>
              ))}
            </FormSection>
          </FormSection>
        </CardContent>

        <CardFooter>
          {!account ? (
            <ButtonLight onClick={toggleWalletModal}>Connect Wallet</ButtonLight>
          ) : isDeploying ? (
            <ButtonPrimary disabled={true}>Deploying...</ButtonPrimary>
          ) : !stakingToken || rewardTokens.every((token) => !token) ? (
            <ButtonPrimary disabled={true}>Enter Reward Token Addresses</ButtonPrimary>
          ) : (
            <ButtonPrimary onClick={() => setShowStartModal(true)} disabled={isDeployDisabled}>
              Deploy Farm
            </ButtonPrimary>
          )}
        </CardFooter>
      </Container>
    </>
  )
}

export default DeployForm
